import React from 'react';

import { Popconfirm, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { NSKeys } from '@app/i18n';
import { CatalogStatus } from '@app/models/products';
import { DropDownMenuButton } from '@components/DropDownMenuButton';

import { useArchiveCatalogQuery, useUnArchiveCatalogQuery } from '../../hooks';

interface IProps {
  catalogUid?: string;
  catalogStatus?: CatalogStatus;
  canUpdate?: boolean;
  type?: React.ComponentProps<typeof DropDownMenuButton>['type'];
  placement?: React.ComponentProps<typeof DropDownMenuButton>['placement'];
}

export const CatalogActionMenu: React.FC<IProps> = ({
  catalogUid,
  catalogStatus,
  canUpdate,
  type = 'default',
  placement = 'bottomRight',
}) => {
  const navigate = useNavigate();
  const catalogsT = useTranslation(NSKeys.catalogs);
  const { mutate: archiveCatalog, isLoading: isArchiveLoading } = useArchiveCatalogQuery();
  const { mutate: unArchiveCatalog, isLoading: isUnArchiveLoading } = useUnArchiveCatalogQuery();

  if (!catalogUid || !canUpdate) {
    return null;
  }

  const canArchive = catalogStatus === CatalogStatus.Scheduled || catalogStatus === CatalogStatus.Active;
  const canUnArchive = catalogStatus === CatalogStatus.Archived;

  return (
    <DropDownMenuButton
      type={type}
      menu={[
        {
          label: <Typography.Text>{catalogsT.t('list.settings')}</Typography.Text>,
          key: catalogUid,
          onClick: () => navigate(`/dashboard/catalogs/edit/${catalogUid}`),
        },
        {
          label: <Typography.Text>{catalogsT.t('list.limits')}</Typography.Text>,
          key: `${catalogUid}-limit`,
          onClick: () => navigate(`/dashboard/catalogs/${catalogUid}/limits`),
        },
        ...(canArchive
          ? [
              {
                disabled: isArchiveLoading,
                label: (
                  <span
                    onClick={(event) => event.stopPropagation()}
                    style={{ display: 'block', margin: '-5px -12px', padding: '5px 12px' }}>
                    <Popconfirm
                      title={catalogsT.t('list.archiveConfirm')}
                      onConfirm={() => archiveCatalog({ catalogUid })}>
                      <Typography.Text style={{ display: 'block' }}>{catalogsT.t('list.archive')}</Typography.Text>
                    </Popconfirm>
                  </span>
                ),
                key: `${catalogUid}-archive`,
              },
            ]
          : []),
        ...(canUnArchive
          ? [
              {
                disabled: isUnArchiveLoading,
                label: (
                  <span
                    onClick={(event) => event.stopPropagation()}
                    style={{ display: 'block', margin: '-5px -12px', padding: '5px 12px' }}>
                    <Popconfirm
                      title={catalogsT.t('list.unArchiveConfirm')}
                      onConfirm={() => unArchiveCatalog({ catalogUid })}>
                      <Typography.Text style={{ display: 'block' }}>{catalogsT.t('list.unArchive')}</Typography.Text>
                    </Popconfirm>
                  </span>
                ),
                key: `${catalogUid}-unArchive`,
              },
            ]
          : []),
      ]}
      placement={placement}
    />
  );
};
